import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db, SECRETS_ENV } from '../../Config/firebase';
import { EventData, OfferType, ServiceProvider, SubscriptionType } from './types/PartnerTypes';
import MinimalEventCarousel from './TryOut/MinimalEventCarousel';
import MinimalOfferCarousel from './TryOut/MinimalOfferCarousel';
import MinimalSubscriptionCarousel from './TryOut/MinimalSubscriptionCarousel';
import { FLAVOR } from '../../Config/firebase';
import { useState } from 'react';
import { useEffect } from 'react';
import { useRef } from 'react';
import { updateSEOTags, generateSharingUrl } from '../../utils/seo';

const PartnerPageMinimal = () => {
  const navigate = useNavigate();

  const [events, setEvents] = useState<EventData[]>([]);
  const [offers, setOffers] = useState<OfferType[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionType[]>([]);
  const [partner, setPartner] = useState<ServiceProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [avgRating, setAvgRating] = useState<number>(4.9);
  const [id, setId] = useState<string>("");
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [customBrandingImage, setCustomBrandingImage] = useState<string | null>(null);
  const [sharingUrl, setSharingUrl] = useState<string>("");

  // Add ref for the treasures section
  const treasuresSectionRef = useRef<HTMLElement>(null);
  const [notFound, setNotFound] = useState(false);
  const [subDomain, setSubDomain] = useState<string>("");

  // Update SEO tags and sharing URL when partner data changes
  useEffect(() => {
    updateSEOTags(partner, customLogo, customBrandingImage);
    
    // Generate a sharing URL for social media platforms
    if (partner) {
      const url = generateSharingUrl(partner, customLogo, customBrandingImage);
      setSharingUrl(url);
    }
  }, [partner, customLogo, customBrandingImage]);

  // Function to handle social media sharing
  const handleShare = (platform: 'twitter' | 'facebook' | 'linkedin' | 'whatsapp') => {
    if (!sharingUrl) return;
    
    const text = partner?.businessProfile?.businessName || partner?.display_name || 'HoSaksham';
    
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(sharingUrl)}&text=${encodeURIComponent(text)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(sharingUrl)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(sharingUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + sharingUrl)}`;
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Optimize the domain check useEffect
  useEffect(() => {
    const host = window.location.host;
    const arr = host.split(".");

    // Check if the first part is 'www' and take the next part as subdomain
    const potentialSubDomain = arr[0] === 'www' ? arr[1] : arr[0];

    if (potentialSubDomain) {
      if (SECRETS_ENV.EXCLUDED_DOMAINS.includes(potentialSubDomain)) {
        // Redirect to /login if the domain is in the excluded list
        navigate('/login');
      } else {
        setSubDomain(potentialSubDomain);
      }
    }
  }, [navigate]);

  // Optimize branding details fetch
  useEffect(() => {
    const fetchBrandingDetails = async () => {
      if (!subDomain) return;

      try {
        const brandingQuery = query(
          collection(db, 'CustomBranding'),
          where('subdomain', '==', `${subDomain}.${SECRETS_ENV.DOMAIN}`)
        );
        const querySnapshot = await getDocs(brandingQuery);

        if (!querySnapshot.empty) {
          const brandingDoc = querySnapshot.docs[0].data();
          setId(brandingDoc.providerReference);
          setCustomLogo(brandingDoc.logo || null);
          setCustomBrandingImage(brandingDoc.brandingImage || null);

        } else {
          // Redirect to /login if no branding details are found
          navigate('/login');
        }
      } catch (error) {
        console.error("Error fetching branding details:", error);
        // Optionally, redirect to /login on error
        navigate('/login');
      }
    };

    fetchBrandingDetails();
  }, [subDomain, navigate]);


  useEffect(() => {
    if (!id) {
      return;
    }

    const fetchPartner = async () => {
      try {
        const docRef = doc(db, 'ServiceProviders', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data() as ServiceProvider;
          setPartner({
            ...data,
            id: docSnap.id,
            created_time: data.created_time ? data.created_time : undefined,
            edited_time: data.edited_time,
            plundrDescription: data.plundrDescription || "",
            plundrImages: data.plundrImages || [],
            plundrRating: data.plundrRating || [],
            plundrNumber: data.plundrNumber || data.phone_number
          });

          if (data.plundrRating.length > 0) {
            const avgRating = data.plundrRating.reduce((acc, curr) => acc + curr.rating, 0) / data.plundrRating.length;
            setAvgRating(avgRating);
          }
        } else {
          setLoading(false);
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error fetching partner:', error);
      }
    };

    const fetchEvents = () => {
      const eventsRef = collection(db, 'ServiceProviders', id, 'Events');
      return onSnapshot(eventsRef, (snapshot) => {
        const eventsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as EventData));
        setEvents(eventsData);
      });
    };

    const fetchOffers = () => {
      const offersRef = collection(db, 'ServiceProviders', id, 'Offers');
      return onSnapshot(offersRef, (snapshot) => {
        const offersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as OfferType));
        setOffers(offersData);
      });
    };

    const fetchSubscriptions = () => {
      const subscriptionsRef = collection(db, 'ServiceProviders', id, 'Subscriptions');
      return onSnapshot(subscriptionsRef, (snapshot) => {
        const subscriptionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as SubscriptionType));
        setSubscriptions(subscriptionsData);
        setLoading(false);
      });
    };

    fetchPartner();
    const unsubscribeEvents = fetchEvents();
    const unsubscribeOffers = fetchOffers();
    const unsubscribeSubscriptions = fetchSubscriptions();



    return () => {
      unsubscribeEvents();
      unsubscribeOffers();
      unsubscribeSubscriptions();
    };
  }, [id]);


  // Function to scroll to treasures section
  const scrollToTreasures = () => {
    treasuresSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  if (loading) {
    return (
      <div className="md:px-12 relative min-h-screen bg-gradient-to-br from-[#FFF1E6] to-[#E8F3E9] overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute top-0 left-1/2 w-[800px] h-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(255,207,175,1) 0%, rgba(255,222,202,0) 70%)',
              animation: 'pulse 4s infinite ease-in-out',
              opacity: '0.2',
              animationDelay: '0s'
            }}
          ></div>
          <div
            className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full blur-3xl"
            style={{
              background: '#9DC4A7',
              animation: 'pulse 4s infinite ease-in-out',
              opacity: '0.2',
              animationDelay: '4s'
            }}
          ></div>
        </div>

        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2V6h4V4H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>

        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            {/* Left side - Text content skeleton */}
            <div className="flex-1 text-center lg:text-left space-y-8">
              <div className="inline-flex items-center rounded-full border border-gray-200 bg-white/50 backdrop-blur-sm px-3 py-1 text-sm text-gray-600 animate-pulse">
                <div className="flex -space-x-2 mr-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="w-6 h-6 rounded-full border-2 border-white overflow-hidden bg-gray-200"></div>
                  ))}
                </div>
                <div className="w-20 h-4 bg-gray-200 rounded"></div>
              </div>

              <div className="space-y-6">
                {/* Title skeleton */}
                <div className="animate-pulse">
                  <div className="h-12 bg-gray-200 rounded-lg w-3/4 mx-auto lg:mx-0 mb-3"></div>
                  <div className="h-12 bg-gray-200 rounded-lg w-1/2 mx-auto lg:mx-0"></div>
                </div>

                {/* Description skeleton */}
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full max-w-xl mx-auto lg:mx-0 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 max-w-xl mx-auto lg:mx-0 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5 max-w-xl mx-auto lg:mx-0"></div>
                </div>

                {/* Buttons skeleton */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-pulse">
                  <div className="h-11 w-40 bg-gray-200 rounded-full"></div>
                  <div className="h-11 w-40 bg-gray-200 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Right side - Images skeleton */}
            <div className="flex-1 relative lg:h-[700px] grid grid-cols-2 gap-4 p-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden shadow-lg bg-gray-200 animate-pulse h-[200px]"></div>
                <div className="rounded-2xl overflow-hidden shadow-lg bg-gray-200 animate-pulse h-[300px]"></div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden shadow-lg bg-gray-200 animate-pulse h-[300px]"></div>
                <div className="rounded-2xl overflow-hidden shadow-lg bg-gray-200 animate-pulse h-[200px]"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Premium Locations Section Skeleton */}
        <section className="py-12 md:py-20 relative z-10 overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#9DC4A7]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D78A6C]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-[#D78A6C]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>

          <div className="container mx-auto px-4 relative">
            <div className="mb-12 text-center animate-pulse">
              <div className="inline-block px-4 py-1.5 bg-gray-200 rounded-full w-40 h-8 mx-auto mb-3"></div>
              <div className="h-8 bg-gray-200 rounded-lg w-3/4 max-w-md mx-auto mb-3"></div>
              <div className="w-24 h-1 bg-gray-200 mx-auto"></div>
            </div>

            <div className="flex flex-wrap justify-center gap-y-4 gap-x-3 sm:gap-x-4 max-w-6xl mx-auto px-2">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div
                  key={i}
                  className="inline-block whitespace-nowrap overflow-hidden text-ellipsis border-2 border-gray-200
                            rounded-lg px-5 py-2 w-24 h-10 animate-pulse bg-gray-200"
                ></div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!id || notFound) return <div className='bg-white h-[100vh] w-full flex justify-center items-center'>No partner found</div>

  // console.log(partner, "partner")



  return (
    <div className="md:px-12 py-10 relative min-h-screen bg-gradient-to-br from-[#FFF1E6] to-[#E8F3E9] overflow-hidden">
      {/* Small Header */}
      <header className="absolute left-0 py-4  px-4 md:px-12  bg-white/10 backdrop-blur-md top-0 z-50 w-full shadow-md border-b border-white/30">
        <div className="container mx-auto">
          <div className="flex items-center">
            <div className="flex items-center space-x-3">
              {customLogo ? (
                <img 
                  src={customLogo} 
                  alt={partner?.businessProfile?.businessName || "Company Logo"} 
                  className="h-8 w-auto object-contain drop-shadow-sm"
                />
              ) : (
                <div className="h-8 w-8 bg-primary/80 rounded-full flex items-center justify-center text-black border  font-bold shadow-sm">
                  {partner?.businessProfile?.businessName?.charAt(0) || "F"}
                </div>
              )}
              <h1 className="text-lg font-sans font-medium text-gray-800 drop-shadow-sm">{partner?.businessProfile?.businessName}</h1>
            </div>
          </div>
        </div>
      </header>

      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>

      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-0 left-1/2 w-[800px] h-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
          style={{
            background: '#D78A6C',
            animation: 'pulse 4s infinite',
            opacity: '0.2'
          }}
        ></div>
        <div
          className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full blur-3xl"
          style={{
            background: '#9DC4A7',
            animation: 'pulse 4s infinite ease-in-out',
            opacity: '0.2',
            animationDelay: '4s'
          }}
        ></div>
      </div>

      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2V6h4V4H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      ></div>
      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          <div className="flex-1 text-center lg:text-left space-y-8">
            <div className="inline-flex items-center rounded-full border border-gray-200 bg-white/50 backdrop-blur-sm px-3 py-1 text-sm text-gray-600 animate-fade-in-down">
              <div className="flex -space-x-2 mr-2">
                {/* Fallback dummy images if partner images are not available */}

                <>
                  <div className="w-6 h-6 rounded-full border-2 border-white overflow-hidden">
                    <img
                      alt="Artisan 1"
                      loading="lazy"
                      width="24"
                      height="24"
                      decoding="async"
                      className="object-cover"
                      src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=64&q=80"
                      style={{ color: 'transparent' }}
                    />
                  </div>
                  <div className="w-6 h-6 rounded-full border-2 border-white overflow-hidden">
                    <img
                      alt="Artisan 2"
                      loading="lazy"
                      width="24"
                      height="24"
                      decoding="async"
                      className="object-cover"
                      src="https://images.unsplash.com/photo-1620436226596-46a359a295bc?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                      style={{ color: 'transparent' }}
                    />
                  </div>
                  <div className="w-6 h-6 rounded-full border-2 border-white overflow-hidden">
                    <img
                      alt="Artisan 3"
                      loading="lazy"
                      width="24"
                      height="24"
                      decoding="async"
                      className="object-cover"
                      src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=64&q=80"
                      style={{ color: 'transparent' }}
                    />
                  </div>
                </>


              </div>
              <div className="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-star w-3.5 h-3.5 text-primary fill-current">
                  <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"></path>
                </svg>
                <span>{avgRating} Rating</span>

              </div>
            </div>
            <div className="space-y-6">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif tracking-tight text-gray-900 animate-fade-in-up">
                <span className="block">Discover</span>
                <span className="block mt-2 text-[#D78A6C]">
                  {partner?.businessProfile?.businessName}
                </span>
              </h1>
              <p className="text-base md:text-xl text-gray-600 max-w-xl mx-auto lg:mx-0 animate-fade-in-up animation-delay-200">
                {partner?.plundrDescription || `${partner?.businessProfile?.businessName} is a place where you can find all the services like ${partner?.ServiceTypes?.join(", ")}. We are a team of professionals who are dedicated to providing you with the best services possible.`}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-400">
                {FLAVOR === "DEVELOPMENT" && <button onClick={() => navigate(`/storefront/${id}`)} className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-11 text-lg px-8 bg-[#D78A6C] hover:bg-[#D78A6C]/90 text-white rounded-2xl group transition-all duration-300 transform hover:translate-x-1">
                  Start Shopping
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-right ml-2 h-5 w-5 transition-transform group-hover:translate-x-1">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </button>}
                <button
                  onClick={scrollToTreasures}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-background h-11 text-lg px-8 border-2 border-[#D78A6C] text-[#D78A6C] hover:bg-[#D78A6C] hover:text-white hover:shadow-lg rounded-2xl transition-all duration-300 transform hover:scale-105">
                  Explore Offerings
                </button>
                {partner?.phone_number && (
                  <a
                    href={`https://wa.me/${partner?.plundrNumber || partner.phone_number.replace(/\D/g, '')}?text=Hi, I'm interested in learning more about your services at ${partner?.businessProfile?.businessName}.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-[#25D366] h-11 text-lg px-8 text-white rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="mr-1">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    Chat on WhatsApp
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="flex-1 relative lg:h-[700px] grid grid-cols-2 gap-4 p-4">
            {(!partner?.plundrImages || partner.plundrImages.length === 0) ? (
              // Placeholder images when no partner images are available
              <>
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                    <img
                      alt="Placeholder craft 1"
                      loading="lazy"
                      width="300"
                      height="300"
                      decoding="async"
                      className="object-cover w-full h-[200px]"
                      src="https://images.unsplash.com/photo-1612100389024-106c3cfdb7d0?q=80&w=1932&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                      style={{ color: 'transparent' }}
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                    <img
                      alt="Placeholder craft 2"
                      loading="lazy"
                      width="300"
                      height="300"
                      decoding="async"
                      className="object-cover w-full h-[300px]"
                      src="https://images.unsplash.com/photo-1571032800240-1cc2e680b00c?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                      style={{ color: 'transparent' }}
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-2xl overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                    <img
                      alt="Placeholder craft 3"
                      loading="lazy"
                      width="300"
                      height="300"
                      decoding="async"
                      className="object-cover w-full h-[300px]"
                      src="https://images.unsplash.com/photo-1609215787811-5b7f079153dc?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                      style={{ color: 'transparent' }}
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                    <img
                      alt="Placeholder craft 4"
                      loading="lazy"
                      width="300"
                      height="300"
                      decoding="async"
                      className="object-cover w-full h-[200px]"
                      src="https://images.unsplash.com/photo-1609773335024-be4301497ea9?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                      style={{ color: 'transparent' }}
                    />
                  </div>
                </div>
              </>
            ) : (
              // Display actual partner images if available
              <>
                <div className="space-y-4">
                  {partner?.plundrImages?.slice(0, 2).map((imgSrc, num) => (
                    <div key={num} className="rounded-2xl overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                      <img
                        alt={`Artisan craft ${num + 1}`}
                        loading="lazy"
                        width="300"
                        height="300"
                        decoding="async"
                        className={`object-cover w-full ${num === 0 ? 'h-[200px]' : 'h-[300px]'}`}
                        src={imgSrc}
                        style={{ color: 'transparent' }}
                      />
                    </div>
                  ))}
                </div>
                <div className="space-y-4 pt-8">
                  {partner?.plundrImages?.slice(2, 4).map((imgSrc, num) => (
                    <div key={num} className="rounded-2xl overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                      <img
                        alt={`Artisan craft ${num + 3}`}
                        loading="lazy"
                        width="300"
                        height="300"
                        decoding="async"
                        className={`object-cover w-full ${num === 0 ? 'h-[300px]' : 'h-[200px]'}`}
                        src={imgSrc}
                        style={{ color: 'transparent' }}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}

          </div>
        </div>
      </div>

      {/* Premium Locations Section */}
      {[...new Set(subscriptions.map(sub => sub.placeName))].filter(Boolean).length > 0 && (
        <section className=" relative z-10 overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#9DC4A7]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D78A6C]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-[#D78A6C]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>

          <div className="container mx-auto px-4 relative">
            <div className="mb-12 text-center">
              <span className="inline-block px-4  bg-[#D78A6C]/10 text-[#D78A6C] rounded-full text-sm font-medium mb-3 animate-fade-in-down shadow-sm">
                {[...new Set(subscriptions.map(sub => sub.placeName))].filter(Boolean).length === 1
                  ? "Our Location"
                  : "Our Premium Network"}
              </span>
              <h2 className="text-2xl md:text-3xl font-serif tracking-tight text-gray-900 mb-3 animate-fade-in-up">
                {[...new Set(subscriptions.map(sub => sub.placeName))].filter(Boolean).length === 1
                  ? "VISIT US AT OUR LOCATION"
                  : "AVAILABLE AT ALL PREMIUM LOCATIONS"}
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-[#D78A6C] to-[#9DC4A7] mx-auto animate-fade-in-up animation-delay-200"></div>
            </div>

            <div className="flex flex-wrap justify-center gap-y-4 gap-x-3 sm:gap-x-4 max-w-6xl mx-auto px-2">
              {/* Extract unique place names from subscriptions */}
              {[...new Set(subscriptions.map(sub => sub.placeName))]
                .filter(Boolean)
                .sort()
                .map((placeName, index) => {
                  // Create a unique but consistent gradient for each location
                  const gradientIndex = placeName.charCodeAt(0) % 5;
                  const gradients = [
                    'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(215,138,108,0.15) 100%)',
                    'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(157,196,167,0.15) 100%)',
                    'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(232,243,233,0.2) 100%)',
                    'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,241,230,0.2) 100%)',
                    'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(186,167,201,0.15) 100%)'
                  ];

                  // For single location, make it more prominent
                  const isSingleLocation = [...new Set(subscriptions.map(sub => sub.placeName))].filter(Boolean).length === 1;

                  return (
                    <div
                      key={index}
                      className={`inline-block whitespace-nowrap overflow-hidden text-ellipsis border-2 border-[#D78A6C]/60 
                                text-gray-800 font-medium rounded-lg px-5 py-2 text-sm md:text-base 
                                hover:bg-[#D78A6C]/10 hover:border-[#D78A6C]/60 transition-all duration-200 
                                cursor-pointer shadow-sm hover:shadow-md transform hover:translate-y-[-2px] 
                                animate-fade-in-up backdrop-blur-sm hover:scale-105 ${isSingleLocation ? 'text-lg md:text-xl py-3 px-8' : ''}`}
                      style={{
                        animationDelay: `${index * 50}ms`,
                        background: gradients[gradientIndex],
                        maxWidth: '100%'
                      }}
                      tabIndex={0}
                      aria-label={`Location: ${placeName}, ${subscriptions?.[0]?.placeAddress}`}
                      onClick={() => { }}
                      onKeyDown={(e) => e.key === 'Enter' && {}}
                    >
                      {isSingleLocation ? `${placeName} ${subscriptions?.[0]?.placeAddress}` : placeName}
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {/* Handpicked Artisanal Treasures section */}
      <section ref={treasuresSectionRef} className="py-16 bg-muted/50 backdrop-blur-sm overflow-hidden">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-rubik font-medium text-center mb-12 animate-fade-in-up">

          </h2>

          {/* Events, Offers, and Subscriptions Carousels */}
          {events.length > 0 && <MinimalEventCarousel events={events} partnerId={id || ""} />}
          {offers.length > 0 && <MinimalOfferCarousel offers={offers} partnerId={id || ""} />}
          {subscriptions.length > 0 && <MinimalSubscriptionCarousel subscriptions={subscriptions} providerId={id || ""} />}

          {/* Show placeholder if no data */}
          {!events.length && !offers.length && !subscriptions.length && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No treasures available at the moment. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter section */}
      <section className="py-16 bg-gradient-to-br from-indigo-50/80 via-white to-gray-50/90 rounded-xl mb-2 shadow-lg ">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-xl md:text-3xl font-sans font-semibold mb-4">Stay Connected with {partner?.businessProfile?.businessName}</h2>
            <div className="mt-6 justify-center flex items-center gap-5">
              <div className="flex items-center justify-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                <div >
                  {partner?.email}
                </div>
              </div>

              <div className="flex items-center justify-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <div >
                {partner?.plundrNumber ||  partner?.phone_number}
                </div>
              </div>
            </div>


          </div>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="py-6 border-t border-gray-200 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-600">
            <div className="mb-4 md:mb-0">
              © 2025 {partner?.businessProfile?.businessName} | All Rights Reserved | Trusted Partner of Hosaksham
            </div>
            <div className="flex space-x-6">
              <a href="https://www.hosaksham.com/privacy-policy" className="hover:text-primary hover:underline transition-colors">
                Privacy Policy
              </a>
              <span>|</span>
              <a href="https://www.hosaksham.com/terms-and-conditions" className="hover:text-primary hover:underline transition-colors">
                Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PartnerPageMinimal;